import { call, put, select, takeLatest } from "redux-saga/effects"
import { putWait, withCallback } from "redux-saga-callback"

import apiClient from "@api/client"
import { IUser } from "@api/schema"
import { Routes } from "@basics/routes"
import { CurrentUserActionTypes, IChangeEmailAction, IChangePasswordAction } from "@redux/actions/currentUser"
import { UNKNOWN_REQUEST_ERROR } from "@redux/common/constants"
import { ScopeTypes } from "@redux/common/reduxTypes"
import { showErrorsInTestEnvironment } from "@redux/common/sagaErrorHelpers"
import { usecaseRequestRunningAction, usecaseRequestSuccessAction } from "@redux/common/scopedRequest/actions"
import { selectCurrentUser } from "@redux/reducer/auth"
import { loadCurrentUserAction, logoutAction } from "@redux/usecases/userAccount/actions"
import { SubmissionError } from "@services/submissionError"
import { BASE_URL } from "config"

export function* currentUserWatcherSaga(): any {
  yield takeLatest(CurrentUserActionTypes.ChangeEmail, withCallback(changeEmailSaga))
  yield takeLatest(CurrentUserActionTypes.ChangePassword, withCallback(changePasswordSaga))
  yield takeLatest(CurrentUserActionTypes.DeleteAccount, withCallback(deleteAccountSaga))
}

/**
 * Returns the current user if already in state or triggers to load it
 */
export function* getCurrentUser(): Generator<any, IUser, any> {
  let currentUser: IUser = yield select(selectCurrentUser)
  if (!currentUser || !currentUser.self) {
    currentUser = yield putWait(loadCurrentUserAction())
  }

  return currentUser
}

/**
 * saga to change users email-address
 */
function* changeEmailSaga(action: IChangeEmailAction) {
  try {
    // the API uses {{param}} as placeholder while Next uses [param]:
    const route = Routes.ConfirmEmailChange.replace(/\[/g, "{{").replace(/\]/g, "}}")
    action.data.verificationUrl = BASE_URL + route

    yield put(usecaseRequestRunningAction(ScopeTypes.UserOperation))
    // verification-URL is given to the API to be included into the mail
    const res: boolean = yield call(apiClient.changeEmail, action.data)
    yield put(usecaseRequestSuccessAction(ScopeTypes.UserOperation, res))
    if (action.actions.onSuccess) {
      yield call(action.actions.onSuccess, res)
    }

    return res
  } catch (err) {
    if (err instanceof SubmissionError) {
      yield call(action.actions.setErrors, err.errors)
    } else if (err instanceof Error) {
      yield call(action.actions.setErrors, { error: err.message })
    }

    const errorMessage = err instanceof Error ? err.message : UNKNOWN_REQUEST_ERROR
    showErrorsInTestEnvironment("changeEmailSaga", errorMessage, action, err)

    yield put(usecaseRequestRunningAction(ScopeTypes.UserOperation, errorMessage))
    if (action.actions.setSubmitting) {
      yield call(action.actions.setSubmitting, false)
    }

    return null
  }
}

/**
 * saga to change users password
 */
function* changePasswordSaga(action: IChangePasswordAction): Generator<any, any, any> {
  try {
    yield put(usecaseRequestRunningAction(ScopeTypes.UserOperation))
    const res: boolean = yield call(apiClient.changePassword, action.data)
    yield put(usecaseRequestSuccessAction(ScopeTypes.UserOperation, res))
    if (action.actions.onSuccess) {
      yield call(action.actions.onSuccess, res)
    }

    return res
  } catch (err) {
    if (err instanceof SubmissionError) {
      yield call(action.actions.setErrors, err.errors)
    } else if (err instanceof Error) {
      yield call(action.actions.setErrors, { error: err.message })
    }

    const errorMessage = err instanceof Error ? err.message : UNKNOWN_REQUEST_ERROR
    showErrorsInTestEnvironment("changePasswordSaga", errorMessage, action, err)

    // signalise to the redux-system: request has stopped
    yield put(usecaseRequestRunningAction(ScopeTypes.UserOperation, errorMessage))
    // signalise to the component, that triggered this saga by Action: submitting stopped
    if (action.actions.setSubmitting) {
      yield call(action.actions.setSubmitting, false)
    }

    return null
  }
}

/**
 * saga to delete a users account
 */
function* deleteAccountSaga(action: IChangePasswordAction) {
  const user: IUser = yield call(getCurrentUser)
  try {
    yield put(usecaseRequestRunningAction(ScopeTypes.UserOperation))
    const res: boolean = yield call(apiClient.deleteEntity, user)
    yield put(usecaseRequestSuccessAction(ScopeTypes.UserOperation, res))
    yield put(logoutAction(Routes.Home, "message.auth.accountDeleted"))

    return res
  } catch (err) {
    if (err instanceof SubmissionError) {
      yield call(action.actions.setErrors, err.errors)
    } else if (err instanceof Error) {
      yield call(action.actions.setErrors, { error: err.message })
    }

    const errorMessage = err instanceof Error ? err.message : UNKNOWN_REQUEST_ERROR
    showErrorsInTestEnvironment("deleteAccountSaga", errorMessage, action, err)
    yield put(usecaseRequestRunningAction(ScopeTypes.UserOperation, errorMessage))
    if (action.actions.setSubmitting) {
      yield call(action.actions.setSubmitting, false)
    }

    return null
  }
}